import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/shadcn/card';
import { Label } from '@/components/shadcn/label.tsx';
import { govtIntakeFormPrimaryObjectOfWork } from '@/data/govtIntakeStaticData.ts';
import type { ObjectType } from '@/modules/governmentIntake';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component';
import { useIntakeForm } from '@/modules/governmentIntake/context/intake-form-context.tsx';

const objectTypeMap: Record<string, ObjectType> = {
  programs: 'programs',
  projects: 'projects',
  properties: 'properties',
};

export function SectionPrimaryObjectOfWork({ onValidationChange }: ValidationChange) {
  const { data, setPrimaryObjectOfWork } = useIntakeForm();

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  const selectedOptions = (data.primaryObjectOfWork ?? []).flatMap((o) => Object.keys(o));

  useEffect(() => {
    onValidationChangeRef.current?.(selectedOptions.length > 0);
  }, [selectedOptions.length]);

  const handleToggle = (rawId: string) => {
    const objectType = objectTypeMap[rawId];
    if (!objectType) return;
    const next = selectedOptions.includes(objectType) ? selectedOptions.filter((o) => o !== objectType) : [...selectedOptions, objectType];
    setPrimaryObjectOfWork(next);
  };

  return (
    <div className='flex flex-col flex-1'>
      <header className='pt-8'>
        <HeaderAndSub
          title='Areas of Work'
          subTitle='Select the areas you regularly support or manage. This helps us tailor follow-up questions.'
          subtitleClass='text-gray-500'
        />
      </header>

      <Label className='text-gray-400 my-4'>Select at least one.</Label>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
        {govtIntakeFormPrimaryObjectOfWork.map((option) => {
          const Icon = option.icon;
          const mapped = objectTypeMap[option.id];
          const isSelected = mapped && selectedOptions.includes(mapped);

          return (
            <Card
              key={option.id}
              onClick={() => handleToggle(option.id)}
              className={`shadow-none border- border-gray-400 cursor-pointer rounded-xl transition-all ${
                isSelected ? 'border-green-800 bg-gray-100 shadow-sm' : 'border-gray-200 bg-white hover:border-green-600'
              }`}
            >
              <CardContent className='px-6 h-full flex flex-col text-lef justify-between gap-6'>
                <div>
                  <div className='flex items-center gap-4 mb-4'>
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-green-900 text-white' : 'bg-gray-100 text-gray-700'}`}>
                      <Icon className='w-8 h-8' />
                    </div>

                    <h3 className='text-base font-semibold text-gray-900'>{option.title}</h3>

                    {isSelected ? <Check className='ml-auto rounded-full p-1 bg-green-900 text-white' /> : null}
                  </div>

                  <p className='self-end text-sm items-start text-gray-600'>{option.description}</p>
                  <Label className='h-px bg-gray-300 my-4 justify-endye' />
                </div>

                <span className='text-gray-400'>
                  Examples: <span className='text-gray-400 text-xs'>{option.examples.join(', ')}</span>
                </span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {selectedOptions.length > 0 && (
        <div className='border border-gray-300 rounded-lg p-4 bg-gray-50 mb-8'>
          <p className='text-sm font-semibold'>Selected areas:</p>

          <div className='flex flex-wrap gap-2 mt-2'>
            {selectedOptions.map((id) => (
              <span key={id} className='border-3 border-gray-300 bg-gray-200 px-2 py-0.5 rounded-lg text-xs text-black'>
                {govtIntakeFormPrimaryObjectOfWork.find((o) => objectTypeMap[o.id] === id)?.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
